/* ═══════════════════════════════════════════════════════════════════════════
   Free Gift (Discovery Set) Picker
   Mirrors Rasasi's selectFreeGift / removeFreeGift / eligibility check,
   adapted to Azha's Dawn-based theme.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  if (window.__apFreeGiftPickerLoaded) return;
  window.__apFreeGiftPickerLoaded = true;

  // Boot log for debugging — verify in DevTools:
  //   window.__apFreeGiftPickerLoaded === true
  //   typeof window.validateCartIntegrity === 'function'
  //   window.freeGiftSettings  // { enabled, threshold, collectionHandle }
  console.log('[ap-free-gift-picker] loaded; freeGiftSettings=', window.freeGiftSettings);

  // ── Section IDs to refresh after cart changes ───────────────────────────
  function getSectionsToRefresh() {
    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems && mainCartItems.dataset.id) {
      sections.push(mainCartItems.dataset.id);
    }
    // Cart page also has the footer (subtotal + checkout button). Without
    // refreshing this section, totals stay stale after the gift add/remove.
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && mainCartFooter.dataset.id) {
      sections.push(mainCartFooter.dataset.id);
    }
    return sections;
  }

  // Move nodes from a freshly parsed Document subtree into a target element.
  // Replaces target's children with the parsed children — equivalent to the
  // pattern Dawn's cart.js uses, but via node import (no innerHTML on a
  // user-reachable element). The HTML source is Shopify's section-render
  // endpoint, which is server-trusted (same trust boundary as the page).
  function replaceTargetChildren(target, sourceElement) {
    if (!target || !sourceElement) return;
    const frag = document.createDocumentFragment();
    Array.from(sourceElement.childNodes).forEach((n) => {
      frag.appendChild(document.importNode(n, true));
    });
    target.replaceChildren(frag);
  }

  function replaceSectionHTML(sectionsMap) {
    if (!sectionsMap) return;
    const parser = new DOMParser();

    if (sectionsMap['cart-drawer']) {
      const drawerInner = document.querySelector('cart-drawer .drawer__inner');
      if (drawerInner) {
        const doc = parser.parseFromString(sectionsMap['cart-drawer'], 'text/html');
        const fresh = doc.querySelector('.drawer__inner');
        replaceTargetChildren(drawerInner, fresh);
      }
    }

    if (sectionsMap['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const doc = parser.parseFromString(sectionsMap['cart-icon-bubble'], 'text/html');
        const fresh = doc.querySelector('.shopify-section');
        replaceTargetChildren(bubble, fresh);
      }
    }

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems && mainCartItems.dataset.id && sectionsMap[mainCartItems.dataset.id]) {
      const doc = parser.parseFromString(sectionsMap[mainCartItems.dataset.id], 'text/html');
      const fresh = doc.querySelector('.js-contents');
      const target = mainCartItems.querySelector('.js-contents');
      replaceTargetChildren(target, fresh);

      // The free-gift picker is rendered as a sibling of #main-cart-items
      // inside the <cart-items> section (see sections/main-cart-items.liquid:
      // <form>...<div id="main-cart-items">...</div></form>
      // <div class="ap-free-gift-slider-cart-container">{% render … %}</div>
      // ...), so swapping only `.js-contents` above leaves the picker stale
      // (still showing the locked / "Select" state after the gift is added).
      // Pull the fresh container out of the same section render and replace
      // its children in-place.
      const freshPickerContainer = doc.querySelector('.ap-free-gift-slider-cart-container');
      const targetPickerContainer = document.querySelector('.ap-free-gift-slider-cart-container');
      console.log('[swap] cart-page picker container', {
        freshFound: !!freshPickerContainer,
        targetFound: !!targetPickerContainer,
        freshHasSelectedCard: !!(freshPickerContainer && freshPickerContainer.querySelector('[data-gift-selected]')),
        freshHasSliderVisible: !!(freshPickerContainer && freshPickerContainer.querySelector('[data-gift-products]:not([hidden])'))
      });
      if (freshPickerContainer && targetPickerContainer) {
        replaceTargetChildren(targetPickerContainer, freshPickerContainer);
      }
    }

    // Cart page footer (subtotal + checkout block). The section renders into
    // a wrapper that contains the id="main-cart-footer" element — pick that
    // element out of the fresh doc and swap our live element's children.
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && mainCartFooter.dataset.id && sectionsMap[mainCartFooter.dataset.id]) {
      const doc = parser.parseFromString(sectionsMap[mainCartFooter.dataset.id], 'text/html');
      const fresh = doc.getElementById('main-cart-footer');
      replaceTargetChildren(mainCartFooter, fresh);
    }
  }

  // ── Notify other listeners (e.g. global.js gift-checkbox handlers) ─────
  // Use source: 'cart-items' so CartItems / CartDrawerItems skip a
  // redundant section refresh — we've already replaced the affected
  // sections inline. The `freeGiftPicker: true` flag lets our own
  // subscriber recognize and ignore self-published events.
  function publishCartUpdate(cartData) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', freeGiftPicker: true, cartData });
      } catch (_) {}
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { freeGiftPicker: true } }));
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  // Tracks cart mutations originating from this script. The cart:changed
  // listener reads this synchronously (before the post-fetch decrement) to
  // distinguish "our own write" (we already drove a section refresh) from
  // "external write" (DevTools, third-party app, second tab, raw fetch from
  // the console) which still needs the validator to enforce gift rules.
  let internalCartMutationDepth = 0;

  async function cartAdd(variantId, quantity, properties) {
    const body = {
      items: [{ id: variantId, quantity, properties }],
      sections: getSectionsToRefresh().join(','),
      sections_url: window.location.pathname
    };
    internalCartMutationDepth++;
    let res;
    try {
      res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
    } finally {
      internalCartMutationDepth--;
    }
    if (!res.ok) {
      let msg = 'Failed to add free gift';
      try { msg = (await res.json()).description || msg; } catch (_) {}
      throw new Error(msg);
    }
    return res.json();
  }

  async function cartUpdate(updates) {
    const body = {
      updates,
      sections: getSectionsToRefresh().join(','),
      sections_url: window.location.pathname
    };
    internalCartMutationDepth++;
    let res;
    try {
      res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
    } finally {
      internalCartMutationDepth--;
    }
    if (!res.ok) throw new Error('Cart update failed');
    return res.json();
  }

  // Fetch the cart sections via the section-render endpoint and swap the DOM.
  // Used as a fallback when an external cart mutation produces no validator
  // remediation (the page UI still needs to reflect the new cart state).
  async function refreshCartSectionsViaRender() {
    const sectionIds = getSectionsToRefresh();
    if (!sectionIds.length) return;
    const url = window.location.pathname + '?sections=' + encodeURIComponent(sectionIds.join(','));
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) return;
    const sectionsMap = await res.json();
    replaceSectionHTML(sectionsMap);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public actions
  // ─────────────────────────────────────────────────────────────────────────

  // Module-level busy flag prevents two near-simultaneous selectFreeGift()
  // calls (e.g. user clicking two different "Select" buttons quickly) from
  // inserting two free gift lines before the cart state reflects the first add.
  let selectingFreeGift = false;

  function readPickerThreshold() {
    const picker = document.querySelector('[data-free-gift-picker]');
    if (picker) {
      const raw = parseFloat(picker.dataset.freeGiftThreshold);
      if (Number.isFinite(raw) && raw > 0) return raw;
    }
    // Fallback for pages that don't render the picker (PLP, PDP, etc.) —
    // injected by layout/theme.liquid from settings.free_gift_threshold so the
    // validator can enforce the threshold rule on every page.
    const fromSettings = window.freeGiftSettings && parseFloat(window.freeGiftSettings.threshold);
    return Number.isFinite(fromSettings) && fromSettings > 0 ? fromSettings : NaN;
  }

  // Qualifying total uses cart.total_price (post-discount, post-cart-level
  // discount codes) minus the existing free-gift line's final_line_price.
  // We deliberately do NOT sum item.original_line_price — that's the catalog
  // price before any discount, so a customer with an automatic discount or
  // /discount/<code> code can cross the threshold on paper while paying well
  // below it. Mirrors Rasasi's formula.
  function computeQualifyingTotal(cart) {
    const existingGift = cart.items.find(
      (item) => item.properties && item.properties._is_free_gift === 'true'
    );
    let cents = Number(cart.total_price) || 0;
    if (existingGift) {
      cents -= Number(existingGift.final_line_price) || 0;
    }
    if (cents < 0) cents = 0;
    return cents / 100;
  }

  async function selectFreeGift(variantId, button) {
    if (selectingFreeGift) {
      console.warn('Free gift selection already in progress, ignoring duplicate click');
      return;
    }
    if (!variantId) return;

    selectingFreeGift = true;
    const originalLabel = button ? button.textContent : '';
    try {
      if (button) {
        button.disabled = true;
        const addingText = button.dataset.addingText || 'Adding...';
        button.textContent = addingText;
      }

      const cart = await fetchCart();
      const existing = cart.items.find(
        (i) => i.properties && i.properties._is_free_gift === 'true'
      );

      // Safety net: fresh cart must clear the threshold. The picker UI gates
      // this, but `selectFreeGift` is globally callable (inline handlers, the
      // console, stale cached HTML, race conditions) so we re-verify here
      // against authoritative cart state.
      const threshold = readPickerThreshold();
      if (Number.isFinite(threshold)) {
        const qualifyingTotal = computeQualifyingTotal(cart);
        if (qualifyingTotal < threshold) {
          console.warn('Free gift refused: qualifying total below threshold', {
            qualifyingTotal,
            threshold
          });
          try { await refreshCartSectionsViaRender(); } catch (_) {}
          return;
        }
      }

      if (existing) {
        await cartUpdate({ [existing.key]: 0 });
      }

      console.log('[select] adding gift variant', variantId);
      const result = await cartAdd(variantId, 1, { _is_free_gift: 'true' });

      // Quick fix for the cart page: in-place DOM swap of the picker
      // container intermittently fails to reflect the Selected state
      // (gift is added correctly but UI keeps showing the slider). Full
      // page reload guarantees the server-rendered Selected state shows.
      // Quick cart drawer and other contexts continue to use the in-place
      // swap since they don't have the issue.
      if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) {
        window.location.reload();
        return;
      }

      await refreshCartSectionsViaRender();
      publishCartUpdate(result);
    } catch (err) {
      console.error('selectFreeGift failed:', err);
      alert(err.message || 'Failed to add free gift');
    } finally {
      selectingFreeGift = false;
      if (button) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    }
  }

  async function removeFreeGift(lineItemKey) {
    try {
      const result = await cartUpdate({ [lineItemKey]: 0 });
      // Same reasoning as selectFreeGift — separate GET render guarantees
      // freshness vs. POST inline sections.
      await refreshCartSectionsViaRender();
      publishCartUpdate(result);
    } catch (err) {
      console.error('removeFreeGift failed:', err);
      alert(err.message || 'Failed to remove free gift');
    }
  }

  async function checkFreeGiftEligibility() {
    try {
      const threshold = readPickerThreshold();
      if (!Number.isFinite(threshold)) return;

      const cart = await fetchCart();
      const existing = cart.items.find(
        (i) => i.properties && i.properties._is_free_gift === 'true'
      );
      if (!existing) return;

      // Use the shared post-discount formula so this legacy path stays
      // consistent with validateCartIntegrity and the snippet.
      const qualifyingTotal = computeQualifyingTotal(cart);

      if (qualifyingTotal < threshold) {
        const result = await cartUpdate({ [existing.key]: 0 });
        replaceSectionHTML(result.sections);
        publishCartUpdate(result);
      }
    } catch (err) {
      console.error('checkFreeGiftEligibility failed:', err);
    }
  }

  // ═════════════════════════════════════════════════════════════════════════
  // CART INTEGRITY VALIDATOR
  // ═════════════════════════════════════════════════════════════════════════
  // Runs on every `cart:changed` event emitted by assets/cart-observer.js —
  // every successful /cart/(add|change|update|clear) request, regardless of
  // source (theme, third-party app, Buy Button, raw fetch from the console).
  // Catches violations that the per-operation checks miss when mutations
  // originate outside ap-free-gift-picker.js.
  //
  // Violations enforced:
  //   1. Multiple free gift lines — keep first, remove the rest.
  //   2. Free gift line with quantity > 1 — clamp to 1 (Shopify merges
  //      repeated raw /cart/add.js POSTs of the same variant+properties into
  //      one line with stacked quantity, so a duplicate-line check alone
  //      misses this — this is the main DevTools attack vector observed in
  //      fake orders with qty 30 free products).
  //   3. Free gift line below threshold — remove gift entirely.
  //
  // Self-throttle: a `cartValidating` flag prevents re-entry while our own
  // remediation cart-updates fire fresh `cart:changed` events, and a
  // coalescing timer collapses bursts (e.g. batch updates) into a single run.
  // ═════════════════════════════════════════════════════════════════════════

  let cartValidating = false;
  let cartValidatePending = false;
  let cartValidateTimer = null;
  // Set when the cart:changed listener observed a mutation originating outside
  // ap-free-gift-picker.js (DevTools, third-party app, /cart/clear.js, second
  // tab). The validator clears it; if no violations were remediated, it also
  // drives a section refresh so the on-page UI reflects the external change.
  let externalCartChangePending = false;

  async function validateCartIntegrity() {
    if (cartValidating) {
      cartValidatePending = true;
      return;
    }
    cartValidating = true;
    try {
      const cart = await fetchCart();
      const updates = {};
      const reasons = [];

      const giftLines = cart.items.filter(
        (i) => i.properties && i.properties._is_free_gift === 'true'
      );
      const nonGiftItemCount = cart.items.reduce(
        (sum, i) =>
          i.properties && i.properties._is_free_gift === 'true' ? sum : sum + i.quantity,
        0
      );

      console.log('[validator] running', {
        giftLineCount: giftLines.length,
        giftQty: giftLines.reduce((s, i) => s + i.quantity, 0),
        nonGiftItemCount,
        cartTotalCents: cart.total_price
      });

      // ── Violation 1: duplicate free gift lines ──
      if (giftLines.length > 1) {
        giftLines.slice(1).forEach((item) => {
          updates[item.key] = 0;
        });
        reasons.push('removed ' + (giftLines.length - 1) + ' duplicate free gift line(s)');
      }

      // ── Violation 2: free gift line with quantity > 1 ──
      // Raw /cart/add.js POSTs of the same variant + properties get merged by
      // Shopify into one line with stacked quantity, bypassing the duplicate-
      // line check above. Clamp the surviving gift line back to qty 1.
      const survivingGift = giftLines.find((i) => updates[i.key] !== 0);
      if (survivingGift && survivingGift.quantity > 1) {
        updates[survivingGift.key] = 1;
        reasons.push('clamped free gift quantity ' + survivingGift.quantity + ' → 1');
      }

      // ── Violation 3: free gift with no other cart items ──
      // Invariant: a free gift only exists alongside a real purchase. This
      // catches the DevTools attack of adding only the gift (cart total = 0)
      // even when the threshold setting is missing from window.freeGiftSettings.
      // Runs before the threshold check so we always defend, regardless of
      // whether settings have hydrated.
      if (survivingGift && updates[survivingGift.key] !== 0 && nonGiftItemCount === 0) {
        updates[survivingGift.key] = 0;
        reasons.push('removed orphan free gift (no other items in cart)');
      }

      // ── Violation 4: free gift below threshold ──
      const threshold = readPickerThreshold();
      if (!Number.isFinite(threshold)) {
        console.warn(
          '[validator] threshold unavailable — readPickerThreshold returned NaN. ' +
            'Check that window.freeGiftSettings is injected by layout/theme.liquid ' +
            'and that ap-free-gift-picker.js is loaded.'
        );
      }
      if (
        survivingGift &&
        updates[survivingGift.key] !== 0 &&
        Number.isFinite(threshold)
      ) {
        const qualifyingTotal = computeQualifyingTotal(cart);
        if (qualifyingTotal < threshold) {
          updates[survivingGift.key] = 0;
          reasons.push(
            'removed ineligible free gift (qualifying ' + qualifyingTotal + ' < ' + threshold + ')'
          );
        }
      }

      if (Object.keys(updates).length > 0) {
        console.log('[validator] violations:', reasons.join('; '), updates);
        const result = await cartUpdate(updates);
        replaceSectionHTML(result.sections);
        publishCartUpdate(result);
        externalCartChangePending = false;
      } else if (externalCartChangePending) {
        console.log('[validator] external mutation, no violations — refreshing UI');
        try {
          await refreshCartSectionsViaRender();
          publishCartUpdate(cart);
        } catch (_) {}
        externalCartChangePending = false;
      } else {
        console.log('[validator] no violations, no UI refresh needed');
      }
    } catch (err) {
      console.error('[validator] failed:', err);
    } finally {
      cartValidating = false;
      if (cartValidatePending) {
        cartValidatePending = false;
        setTimeout(validateCartIntegrity, 0);
      }
    }
  }

  function scheduleCartValidation() {
    if (cartValidateTimer) clearTimeout(cartValidateTimer);
    cartValidateTimer = setTimeout(() => {
      cartValidateTimer = null;
      validateCartIntegrity();
    }, 250);
  }

  // Listen for the universal cart:changed event emitted by cart-observer.js.
  // IMPORTANT: capture `internalCartMutationDepth` synchronously here — it is
  // decremented in `cartAdd` / `cartUpdate`'s finally block, which runs after
  // this event dispatch but before any setTimeout fires.
  document.addEventListener('cart:changed', () => {
    if (internalCartMutationDepth === 0) {
      externalCartChangePending = true;
    }
    scheduleCartValidation();
  });

  // Expose for debugging / on-demand checks.
  window.validateCartIntegrity = validateCartIntegrity;

  // ─────────────────────────────────────────────────────────────────────────
  // Swiper initialization — re-runs after every section refresh because the
  // picker DOM is replaced and prior Swiper instances are detached.
  // ─────────────────────────────────────────────────────────────────────────
  function initSwipers() {
    if (typeof Swiper === 'undefined') return;
    document.querySelectorAll('.ap-fgp-swiper').forEach((el) => {
      // Destroy any stale instance attached to this element so a fresh
      // layout pass runs on the current container width. Without this,
      // re-renders after Remove can leave Swiper computing zero-width
      // slides (the "one big item" symptom).
      if (el.swiper) {
        try { el.swiper.destroy(true, true); } catch (_) {}
      }
      const wrapper = el.closest('.ap-fgp-slider-wrapper');
      const nextEl = wrapper && wrapper.querySelector('[data-fgp-next]');
      const prevEl = wrapper && wrapper.querySelector('[data-fgp-prev]');

      // Only bail when the slider sits inside the explicit hidden
      // "Change" subtree. Otherwise let Swiper init even when the cart
      // drawer is currently closed — `observer: true` will recompute
      // when it becomes visible. (The previous offsetParent guard was
      // too aggressive and skipped init while the drawer was just
      // closed at load time.)
      const hiddenWrapper = el.closest('[data-gift-products][hidden]');
      if (hiddenWrapper) return;

      const swiper = new Swiper(el, {
        slidesPerView: 2.2,
        spaceBetween: 8,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        navigation: nextEl && prevEl ? { nextEl, prevEl } : false,
        breakpoints: {
          750: { slidesPerView: 2.5, spaceBetween: 10 },
          1024: { slidesPerView: 3.5, spaceBetween: 12 }
        }
      });

      // Force a measurement pass after the next paint so the slides
      // settle on their final width, even if init happened before
      // layout fully stabilized.
      requestAnimationFrame(() => {
        try { swiper.update(); } catch (_) {}
      });
    });
  }

  // Schedule init across two animation frames to guarantee the freshly
  // injected DOM has been laid out before Swiper measures it.
  function scheduleInit() {
    requestAnimationFrame(() => {
      requestAnimationFrame(initSwipers);
    });
  }

  // Initial init with retry while Swiper script may still be loading.
  function initWhenReady() {
    if (typeof Swiper !== 'undefined') {
      scheduleInit();
      return;
    }
    let attempts = 0;
    const t = setInterval(() => {
      attempts += 1;
      if (typeof Swiper !== 'undefined') {
        scheduleInit();
        clearInterval(t);
      } else if (attempts >= 20) {
        clearInterval(t);
      }
    }, 100);
  }

  window.initApFreeGiftSwipers = initSwipers;

  // ─────────────────────────────────────────────────────────────────────────
  // Event delegation — picker DOM is replaced on every cart refresh, so
  // delegated listeners on document survive.
  // ─────────────────────────────────────────────────────────────────────────
  document.addEventListener('click', (e) => {
    const selectBtn = e.target.closest('[data-select-gift]');
    if (selectBtn) {
      e.preventDefault();
      selectFreeGift(selectBtn.dataset.selectGift, selectBtn);
      return;
    }

    const removeBtn = e.target.closest('[data-remove-gift]');
    if (removeBtn) {
      e.preventDefault();
      removeFreeGift(removeBtn.dataset.removeGift);
      return;
    }

    const changeBtn = e.target.closest('[data-change-gift]');
    if (changeBtn) {
      e.preventDefault();
      const wrapper = changeBtn.closest('[data-free-gift-picker]');
      if (!wrapper) return;
      const products = wrapper.querySelector('[data-gift-products]');
      const selected = wrapper.querySelector('[data-gift-selected]');
      if (products) products.hidden = false;
      if (selected) selected.style.display = 'none';
      // Slider was hidden; init now that it's visible (after layout)
      scheduleInit();
    }
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Hook into Azha's cart events: auto-remove gift below threshold AND
  // re-init Swiper after cart-driven section refreshes.
  // ─────────────────────────────────────────────────────────────────────────
  function wireCartHooks() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Always re-init Swiper because the picker DOM was just replaced.
        // Wait two animation frames so layout has stabilized first.
        scheduleInit();
        // Skip eligibility re-check if we just published this event ourselves.
        if (event && event.freeGiftPicker) return;
        checkFreeGiftEligibility();
      });
    }
    document.addEventListener('cart:updated', (e) => {
      scheduleInit();
      if (e.detail && e.detail.freeGiftPicker) return;
      checkFreeGiftEligibility();
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Safety net: other Azha scripts (refreshCartSectionstheme,
  // CartItems.onCartUpdate, etc.) replace cart-drawer-items and
  // main-cart-items via replaceWith, which detaches our picker DOM along
  // with its Swiper instance. Watch for fresh `.ap-fgp-swiper` nodes
  // appearing anywhere in the document and re-init.
  // ─────────────────────────────────────────────────────────────────────────
  function watchForPickerInsertions() {
    if (typeof MutationObserver === 'undefined') return;
    const seen = new WeakSet();
    const insertionObserver = new MutationObserver((mutations) => {
      let needsInit = false;
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue; // element nodes only
          const isSwiperEl = node.matches && node.matches('.ap-fgp-swiper');
          const containsSwiper = node.querySelector && node.querySelector('.ap-fgp-swiper');
          if ((isSwiperEl || containsSwiper) && !seen.has(node)) {
            seen.add(node);
            needsInit = true;
          }
        }
      }
      if (needsInit) scheduleInit();
    });
    insertionObserver.observe(document.body, { childList: true, subtree: true });

    // Also re-init when the cart drawer toggles open — Swiper measures
    // zero width while the drawer is closed/transitioning, so we must
    // re-measure once it's actually visible.
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      const drawerObserver = new MutationObserver(() => {
        if (drawer.classList.contains('active')) {
          scheduleInit();
        }
      });
      drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function bootstrap() {
    wireCartHooks();
    watchForPickerInsertions();
    initWhenReady();
    // Run a full integrity check on every page load. Catches state mutated
    // outside this tab (second browser tab, /discount/<code> applied between
    // visits, abandoned-cart recovery, third-party app cart writes) — e.g. a
    // qty-30 free gift line stamped from a previous DevTools attack.
    validateCartIntegrity();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Expose for parity with Rasasi
  window.selectFreeGift = selectFreeGift;
  window.removeFreeGift = removeFreeGift;
  window.checkFreeGiftEligibility = checkFreeGiftEligibility;
})();
